import React, { useState, useRef } from "react";
import InstantSearch from "@herb/components/Search/InstantSearch";
import { Index, connectInfiniteHits, Configure } from "react-instantsearch-dom";
import Button from "@herb/components/Button";
import TrendingArticleCard from "../../Cards/TrendingArticleCard";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import usePrefetchedData from "../../../hooks/usePrefetchedData";

const isBrowser = typeof window !== "undefined";
const documentCheck = typeof document !== "undefined";

const LatestArticleLoadAlgolia = ({ posts, lazyLoad = true }) => {
  const [showPagination, setShowPagination] = useState(false);
  const visibleItemCountRef = useRef(4); // Start showing 4 items (articles 5-8 after skipping first 4)
  const { data: prefetchedArticles, isLoading: prefetchLoading } = usePrefetchedData("articles");
  
  // Lazy-load this component - only fetch from Algolia when scrolled into view
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  const handleMoreClick = () => {
    setShowPagination(true);
  };

  // Define CustomHits inside component so it can access and update the ref
  const CustomHits = connectInfiniteHits(({ hits, hasMore, refine }) => {
    // ALWAYS skip first 4 (prefetched articles)
    // Show only items within the visible window: [4, 4 + visibleItemCountRef.current]
    const paginatedHits = hits.slice(4, 4 + visibleItemCountRef.current);
    
    return (
      <React.Fragment>
        {paginatedHits.map(article => (
          <TrendingArticleCard article={article} key={article.id} />
        ))}
        {hasMore && (
          <div className="moreButton">
            <Button
              type={"buttonMore"}
              to="/read/"
              link
              onClick={e => {
                e.preventDefault();
                visibleItemCountRef.current += 4;
                refine();
              }}
            >
              more
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  });

  // If we have prefetched data, show it immediately
  const shouldShowPrefetched = !prefetchLoading && prefetchedArticles?.length > 0;

  const renderPrefetchedContent = () => (
    <div>
      {prefetchedArticles?.map(article => (
        <TrendingArticleCard article={article} key={article.objectID || article.id} />
      ))}
    </div>
  );

  // Algolia component for pagination
  // Uses visibleItemCountRef to show only a window of results
  // First call: fetch 8 (skip 4, show 4)  
  // Subsequent calls: fetch 4 more each time
  const renderAlgoliaContent = () => (
    <div id="algolia">
      <InstantSearch>
        <Index indexName="Dev_Articles">
          <Configure
            aroundLatLngViaIP={false}
            hitsPerPage={visibleItemCountRef.current > 4 ? 4 : 8}
            query="*"
            filters={"NOT categories:strains AND NOT categories:recipes"}
          />
          <CustomHits />
        </Index>
      </InstantSearch>
    </div>
  );

  const renderContent = () => (
    <div id="algolia">
      {/* Always show prefetched articles first */}
      {shouldShowPrefetched && renderPrefetchedContent()}
      
      {/* Show "More" button only if not showing pagination */}
      {shouldShowPrefetched && !showPagination && (
        <div className="moreButton">
          <Button
            type={"buttonMore"}
            to="/read/"
            link
            onClick={e => {
              e.preventDefault();
              handleMoreClick();
            }}
          >
            more
          </Button>
        </div>
      )}
      
      {/* Show Algolia pagination after "More" is clicked */}
      {showPagination && renderAlgoliaContent()}
    </div>
  );

  // Wrap with lazy-loading if enabled
  if (lazyLoad) {
    return (
      <div ref={ref}>
        {isVisible ? (
          renderContent()
        ) : (
          <div css={{ minHeight: "400px" }} />
        )}
      </div>
    );
  }

  // No lazy-load (for article pages where we want immediate load)
  return (
    <>
      {renderContent()}

      {/* <div id="wordpress" style={{ display: "unset" }}>
        {posts?.slice(0, 4)?.map(article => (
          <TrendingArticleCard article={article} key={article.id} />
        ))}

        <div className="moreButton">
          <Button
            type={"buttonMore"}
            to="/read/"
            link
            onClick={e => {
              e.preventDefault();
              handleMoreClick();
            }}
          >
            more
          </Button>
        </div>
      </div> */}
    </>
  );
};

export default LatestArticleLoadAlgolia;
